import Cookies from "js-cookie";
import { ZodType, z } from "zod";

interface IBrowserStorage<T> {
  get: (key: string) => T | null;
  set: (key: string, value: T) => void;
}

type ClippedStorage = Pick<Storage, "getItem" | "setItem">;

class BrowserStorage<T, S extends ZodType> implements IBrowserStorage<T> {
  protected storage: ClippedStorage;
  protected schema: S;

  constructor(storage: ClippedStorage, schema: S) {
    this.storage = storage;
    this.schema = schema;
  }

  get(key: string): z.infer<S> | null {
    const stringValue = this.storage.getItem(key);
    const value = stringValue ? JSON.parse(stringValue) : null;
    const result = this.schema.safeParse(value);
    if (result.success) {
      return value;
    }

    return null;
  }

  set(key: string, value: z.infer<S>): void {
    this.storage.setItem(key, JSON.stringify(value));
  }
}

class BaseBrowserStorageCell<T, S extends ZodType> {
  private storage: IBrowserStorage<T>;
  private key: string;

  constructor(storage: ClippedStorage, schema: S, key: string) {
    this.key = key;

    this.storage = new BrowserStorage(storage, schema);
  }

  get(): z.infer<S> | null {
    return this.storage.get(this.key);
  }

  set(value: z.infer<S>): void {
    this.storage.set(this.key, value);
  }
}

class BlankStorage implements ClippedStorage {
  getItem(): null {
    return null;
  }

  setItem(): void {
    return;
  }
}

export class LocalStorageCell<
  T,
  Shcema extends ZodType,
> extends BaseBrowserStorageCell<T, Shcema> {
  constructor(schema: Shcema, key: string) {
    const storage =
      typeof window === "undefined" ? new BlankStorage() : localStorage;
    super(storage, schema, key);
  }
}

export class SessionStorageCell<
  T,
  Schema extends ZodType,
> extends BaseBrowserStorageCell<T, Schema> {
  constructor(schema: Schema, key: string) {
    const storage =
      typeof window === "undefined" ? new BlankStorage() : sessionStorage;
    super(storage, schema, key);
  }
}

export class ClientOnlyCookieStorageCell<
  T,
  Schema extends ZodType,
  O extends Parameters<typeof Cookies.set>[2],
> extends BaseBrowserStorageCell<T, Schema> {
  constructor(schema: Schema, key: string, cookieOptions?: O) {
    const storage =
      typeof window === "undefined"
        ? new BlankStorage()
        : {
            getItem: (key: string) => {
              return Cookies.get(key) ?? null;
            },

            setItem: (key: string, value: z.infer<Schema>) => {
              Cookies.set(key, value, cookieOptions);
            },
          };
    super(storage, schema, key);
  }
}
